import React, { useState } from "react";

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ErrorIcon,
  SpinnerIcon,
} from "@/components/icons";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/utils/cn";
import type { Notification, NotificationSeverity } from "@/types/notifications";
import {
  formatRelativeTime,
  severityClasses,
  severityLabel,
} from "./NotificationPanel";

const BODY_PREVIEW_CHARS = 160;

interface NotificationsModalProps {
  isOpen: boolean;
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onItemClick?: (n: Notification) => void;
  onClose: () => void;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  notifications,
  isLoading,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onItemClick,
  onClose,
}) => {
  const header = (
    <div className="flex items-center gap-2">
      <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
      {unreadCount > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
          {unreadCount}
        </span>
      )}
      {unreadCount > 0 && (
        <button
          onClick={onMarkAllRead}
          className="ml-3 text-xs text-blue-600 hover:text-blue-800"
        >
          Mark all read
        </button>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} header={header} size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-sm text-gray-500 gap-2">
          <SpinnerIcon className="w-4 h-4" />
          Loading…
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center text-gray-500 text-sm">
          No notifications.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {notifications.map((n) => (
            <NotificationRow
              key={n.id}
              notification={n}
              onMarkRead={onMarkRead}
              onItemClick={onItemClick}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
};

interface NotificationRowProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onItemClick?: (n: Notification) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  notification: n,
  onMarkRead,
  onItemClick,
}) => {
  const [expanded, setExpanded] = useState(false);

  const sev = (n.severity || "info") as NotificationSeverity;
  const isUnread = n.readAt === null;
  const body = n.body ?? "";
  const isLong = body.length > BODY_PREVIEW_CHARS;
  const shown =
    expanded || !isLong ? body : `${body.slice(0, BODY_PREVIEW_CHARS).trimEnd()}…`;

  const handleRowClick = () => {
    if (isUnread) onMarkRead(n.id);
    onItemClick?.(n);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <li
      className={cn(
        "px-6 py-4 transition-colors",
        onItemClick && "cursor-pointer hover:bg-gray-50",
        isUnread && "bg-blue-50/30",
      )}
      onClick={onItemClick ? handleRowClick : undefined}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "shrink-0 mt-0.5 w-8 h-8 rounded-md flex items-center justify-center",
            severityClasses[sev],
          )}
          title={severityLabel[sev]}
        >
          {sev === "error" ? (
            <ErrorIcon className="w-4 h-4" />
          ) : sev === "success" ? (
            <CheckIcon className="w-4 h-4" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-current" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p
              className={cn(
                "text-sm",
                isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-700",
              )}
            >
              {n.title}
            </p>
            <span className="text-xs text-gray-500 shrink-0">
              {formatRelativeTime(n.createdAt)}
            </span>
          </div>
          {body && (
            <>
              <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
                {shown}
              </p>
              {isLong && (
                <button
                  type="button"
                  onClick={handleToggle}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  {expanded ? (
                    <ChevronDownIcon className="w-3 h-3" />
                  ) : (
                    <ChevronRightIcon className="w-3 h-3" />
                  )}
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
};
